import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Alert,
  Stack
} from '@mui/material'
import { submitDispute } from '../../services/disputeService'

const DISPUTE_REASONS = [
  'Dive did not take place',
  'Host was a no-show',
  'Experience did not match description',
  'Safety concerns during dive',
  'Charged incorrectly',
  'Other'
]

export interface DisputeFormProps {
  open: boolean
  onClose: () => void
  diveId: string
}

const DisputeForm = ({ open, onClose, diveId }: DisputeFormProps) => {
  const [reason, setReason] = useState('')
  const [details, setDetails] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const canSubmit = reason.trim().length > 0 && details.trim().length >= 10

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)

    try {
      await submitDispute({ diveId, reason, details: details.trim() })
      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit dispute')
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (submitting) return
    setReason('')
    setDetails('')
    setError(null)
    setSuccess(false)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>Raise a Dispute</DialogTitle>
      <DialogContent>
        {success ? (
          <Alert severity='success' sx={{ mt: 1 }}>
            Your dispute has been submitted. We&apos;ll review it and get back
            to you.
          </Alert>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity='error'>{error}</Alert>}
            <TextField
              select
              label='Reason'
              value={reason}
              onChange={e => setReason(e.target.value)}
              fullWidth
              required
            >
              {DISPUTE_REASONS.map(r => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label='Details'
              value={details}
              onChange={e => setDetails(e.target.value)}
              multiline
              rows={4}
              fullWidth
              required
              helperText='Please provide at least 10 characters describing the issue'
              placeholder='Describe what happened and what resolution you are seeking...'
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {success ? 'Close' : 'Cancel'}
        </Button>
        {!success && (
          <Button
            onClick={handleSubmit}
            variant='contained'
            disabled={!canSubmit || submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Dispute'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default DisputeForm
